import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  LinearProgress,
  Alert
} from '@mui/material';

export default function ClipExportDialog({
  open,
  onClose,
  episode,
  isMobile,
  startTime,
  endTime,
  error,
  exporting,
  progress,
  progressMessage,
  estimatedSize,
  onStartTimeChange,
  onEndTimeChange,
  onUseCurrentPosition,
  onExport
}) {
  if (!episode) return null;

  return (
    <Dialog
      open={open}
      onClose={exporting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          m: { xs: 0, sm: 2 },
          maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' },
          pt: { xs: 'env(safe-area-inset-top)', sm: 0 }
        }
      }}
    >
      <DialogTitle sx={{ px: { xs: 2, sm: 3 }, overflowWrap: 'anywhere' }}>
        Export Clip
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
        <Typography
          variant="body2"
          color="text.secondary"
          gutterBottom
          sx={{ overflowWrap: 'anywhere' }}
        >
          {episode.title}
        </Typography>

        {isMobile && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Shorter clips export more reliably on mobile devices.
          </Alert>
        )}

        <Box
          sx={{
            mt: 2,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2
          }}
        >
          <TextField
            label="Start Time"
            value={startTime}
            onChange={(event) => onStartTimeChange(event.target.value)}
            placeholder="MM:SS"
            size="small"
            fullWidth
            disabled={exporting}
          />
          <TextField
            label="End Time"
            value={endTime}
            onChange={(event) => onEndTimeChange(event.target.value)}
            placeholder="MM:SS"
            size="small"
            fullWidth
            disabled={exporting}
          />
        </Box>

        <Button
          size="small"
          onClick={onUseCurrentPosition}
          disabled={exporting}
          sx={{ mt: 1 }}
        >
          Use current position
        </Button>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Estimated size: ~{estimatedSize} MB
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {exporting && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              {progressMessage} ({Math.round(progress)}%)
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          pb: { xs: 'calc(16px + env(safe-area-inset-bottom))', sm: 2 },
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'stretch',
          '& .MuiButton-root': {
            width: { xs: '100%', sm: 'auto' }
          }
        }}
      >
        <Button onClick={onClose} disabled={exporting}>
          Cancel
        </Button>
        <Button onClick={onExport} variant="contained" disabled={exporting}>
          {exporting ? 'Exporting...' : 'Download Clip'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
